import React from 'react';
import classNames from 'classnames';
import { Button } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import PencilIcon from 'icons/Pencil.icon';
import { checkIsValidEmail } from 'utils';
import { COLOR_VARIANT } from 'models/Colors';
import {
  PARSED_SPREADSHEET_RFI_ROW_STATUS,
  ParsedSpreadsheetRFIRow,
} from 'router/subrouters/DataRoom/models/DataRoomRfi';

export type ParsedRow = ParsedSpreadsheetRFIRow & Record<'edit', string>;

const PARSED_SPREADSHEET_RFI_ROW_STATUS_COLOR: Record<string, string> = {
  [PARSED_SPREADSHEET_RFI_ROW_STATUS.READY_TO_ADD]: COLOR_VARIANT.GREEN,
  [PARSED_SPREADSHEET_RFI_ROW_STATUS.NEEDS_ATTENTION]: COLOR_VARIANT.RED,
};

type ParsedRowColumn =
  | Exclude<keyof ParsedSpreadsheetRFIRow, 'errors'>
  | 'edit';

type CellProps = {
  row: ParsedSpreadsheetRFIRow;
  onClick: () => void;
};

export const TABLE_COLUMNS: Array<keyof ParsedRow> = [
  'document',
  'assignedTo',
  'note',
  'status',
  'edit',
];

export const HEADING_TRANSLATIONS: Partial<Record<keyof ParsedRow, string>> = {
  document: 'documentColumn',
  assignedTo: 'assignedTo',
  note: 'note',
  status: 'status',
};

export const SORTING_VALUES: Partial<Record<keyof ParsedRow, string>> = {
  document: 'document',
  assignedTo: 'assignedTo',
  status: 'status',
};

export const CUSTOM_COL_SIZES: Partial<Record<keyof ParsedRow, number>> = {
  edit: 60,
};

const DocumentCell: React.FC<CellProps> = ({ row }) => (
  <span
    className={classNames('parsed-documents-table__cell--truncate', {
      'parsed-documents-table__cell--invalid': !row.document,
    })}
  >
    {row.document || '/'}
  </span>
);

const AssignedToCell: React.FC<CellProps> = ({ row }) => (
  <span
    className={classNames('parsed-documents-table__cell--truncate', {
      'parsed-documents-table__cell--invalid': !checkIsValidEmail(
        row.assignedTo,
      ),
    })}
  >
    {row.assignedTo || '/'}
  </span>
);

const NoteCell: React.FC<CellProps> = ({ row }) => (
  <span className="parsed-documents-table__cell--muted">{row.note || '/'}</span>
);

const StatusCell: React.FC<CellProps> = ({ row }) => {
  const { t } = useTranslation();

  const isReady = row.status === PARSED_SPREADSHEET_RFI_ROW_STATUS.READY_TO_ADD;

  return (
    <span
      className={classNames(
        'parsed-documents-table__status',
        PARSED_SPREADSHEET_RFI_ROW_STATUS_COLOR[row.status],
      )}
    >
      {t(isReady ? 'readyToAddStatus' : 'needsAttentionStatus')}
    </span>
  );
};

const EditCell: React.FC<CellProps> = ({ onClick }) => {
  const { t } = useTranslation();

  return (
    <Button
      type="button"
      variant="icon"
      icon={PencilIcon}
      aria-label={t('edit')}
      onClick={onClick}
    />
  );
};

export const BODY_CONTENT_GENERATE: Record<
  ParsedRowColumn,
  (cellProps: CellProps) => React.ReactNode
> = {
  document: (cellProps) => <DocumentCell {...cellProps} />,
  assignedTo: (cellProps) => <AssignedToCell {...cellProps} />,
  note: (cellProps) => <NoteCell {...cellProps} />,
  status: (cellProps) => <StatusCell {...cellProps} />,
  edit: (cellProps) => <EditCell {...cellProps} />,
};
